import { useEffect } from "react";
import { isRouteReady, preloadRoute } from "./routeChunks";

// Loads a page's code once the visitor shows they are about to open it, so the
// split bundle rarely makes a navigation wait (see routeChunks.jsx).
//
// "About to open it" means: the pointer has rested on a link for a moment while
// the page is NOT scrolling, the button went down on it, or keyboard focus
// reached it. Loading a chunk runs its code and inserts its stylesheet, which
// restyles the whole document - measured on the Work page, where every card is a
// link, prefetching whatever slid under a resting pointer during a scroll
// raised scroll-time paint and style work by more than half. So nothing loads
// mid-scroll, and there is no speculative loading on touch: a tap loads the page
// itself. The two likeliest next pages (Work, a case study) are downloaded - not
// run - ahead of time by webpackPrefetch in routeChunks.jsx.
//
// Renders nothing.

const HOVER_INTENT_MS = 120;
const SCROLL_QUIET_MS = 300;

const linkPath = (target) => {
  const link = target instanceof Element ? target.closest("a[href]") : null;
  if (!link) return null;
  const { origin, pathname } = new URL(link.href, window.location.href);
  if (origin !== window.location.origin) return null;
  if (pathname.startsWith("/demos/") || pathname.startsWith("/Assets/")) return null;
  return pathname;
};

const load = (pathname) => {
  if (pathname && !isRouteReady(pathname)) preloadRoute(pathname).catch(() => {});
};

const RoutePrefetch = () => {
  useEffect(() => {
    let lastScroll = 0;
    let hoverTimer = 0;

    const onScroll = () => {
      lastScroll = performance.now();
    };
    const onOver = (event) => {
      clearTimeout(hoverTimer);
      const pathname = linkPath(event.target);
      if (!pathname) return;
      hoverTimer = setTimeout(() => {
        if (performance.now() - lastScroll >= SCROLL_QUIET_MS) load(pathname);
      }, HOVER_INTENT_MS);
    };
    const onDown = (event) => {
      if (event.pointerType !== "touch") load(linkPath(event.target));
    };
    const onFocus = (event) => load(linkPath(event.target));

    const options = { capture: true, passive: true };
    window.addEventListener("scroll", onScroll, options);
    document.addEventListener("pointerover", onOver, options);
    document.addEventListener("pointerdown", onDown, options);
    document.addEventListener("focusin", onFocus, options);

    return () => {
      clearTimeout(hoverTimer);
      window.removeEventListener("scroll", onScroll, options);
      document.removeEventListener("pointerover", onOver, options);
      document.removeEventListener("pointerdown", onDown, options);
      document.removeEventListener("focusin", onFocus, options);
    };
  }, []);

  return null;
};

export default RoutePrefetch;
